import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import PaginationFooter from "./PaginationFooter";
import { usePaginatedList } from "../../hooks/usePaginatedList";

type PaginationMeta = {
  page: number;
  limit: number;
  total: number;
  pages: number;
  hasNext: boolean;
  hasPrev: boolean;
};

type PaginatedApiResponse<T> = {
  data: T[];
  pagination: PaginationMeta;
};

export interface ServerPaginationProps<T> {
  endpoint?: string;
  search?: string;
  extraParams?: Record<string, unknown>;
  fetchPage?: (page: number, limit: number) => Promise<unknown>;
  watch?: unknown;
  children: (items: T[], helpers?: { refetch: () => void }) => React.ReactNode;

  initialPage?: number;
  initialLimit?: number;
  pageSizeOptions?: number[];
  emptyMessage?: string;
  loadingMessage?: string;
  showCount?: boolean;
}

export function ServerPagination<T>({
  endpoint,
  search,
  children,
  initialPage = 1,
  initialLimit = 10,
  pageSizeOptions = [5, 10, 15, 20],
  emptyMessage = "No results found",
  loadingMessage = "Loading...",
  showCount = true,
  extraParams,
}: ServerPaginationProps<T>) {
  // Build query parameters
  const queryParams = React.useMemo(() => {
    const params: Record<string, string> = {};
    if (search) {
      params.search = search;
    }
    if (extraParams) {
      Object.entries(extraParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params[key] = String(value);
        }
      });
    }
    return params;
  }, [search, extraParams]);

  const { items, total, page, limit, setPage, setLimit, loading, error, refetch } =
    usePaginatedList<T>({
      endpoint: endpoint || '',
      page: initialPage,
      limit: initialLimit,
      extraParams: queryParams,
    });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {loading ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            {loadingMessage}
          </Typography>
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            Error: {String(error)}
          </Typography>
        </Box>
      ) : items.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        children(items, { refetch })
      )}

      <PaginationFooter
        total={total}
        page={page}
        limit={limit}
        onPageChange={setPage}
        onLimitChange={(v) => {
          setLimit(v);
          setPage(1);
        }}
        pageSizeOptions={pageSizeOptions}
        showCount={showCount}
      />
    </Box>
  );
}

export default ServerPagination;